// Note: Using direct HTTP calls to AgentCore with JWT bearer tokens
// as shown in AWS AgentCore documentation
// IMPORTANT: Use Access Token (not ID Token) because AgentCore expects 'client_id' claim
// Cognito Access Tokens have 'client_id', while ID Tokens have 'aud' (audience)
import { getAccessToken } from './auth';

const region = import.meta.env.VITE_REGION || 'us-east-1';
const agentRuntimeArn = import.meta.env.VITE_AGENT_RUNTIME_ARN;
const agentBaseUrl = import.meta.env.VITE_AGENT_BASE_URL; // Optional: for local development

/**
 * Get the base URL for agent API calls
 * - If VITE_AGENT_BASE_URL is set (local mode): use it directly
 * - Otherwise (production mode): construct AWS Bedrock AgentCore URL
 */
function getAgentBaseUrl(): string {
  if (agentBaseUrl) {
    // Local mode - use provided URL (e.g., http://localhost:8080)
    console.log('🔧 Using local agent URL:', agentBaseUrl);
    return agentBaseUrl;
  } else {
    // Production mode - construct AWS URL
    if (!agentRuntimeArn) {
      throw new Error('AgentCore Runtime ARN not configured. Please check deployment.');
    }
    const encodedArn = encodeURIComponent(agentRuntimeArn);
    const url = `https://bedrock-agentcore.${region}.amazonaws.com/runtimes/${encodedArn}`;
    console.log('🔧 Using production agent URL:', url);
    return url;
  }
}

/**
 * Get the full endpoint URL for agent invocations
 * - Local: /invocations (uses Vite proxy to avoid CORS)
 * - Production: https://bedrock-agentcore.../invocations?qualifier=DEFAULT
 */
function getAgentEndpoint(): string {
  const baseUrl = getAgentBaseUrl();
  const isLocal = baseUrl.startsWith('http://localhost') || baseUrl.startsWith('http://127.0.0.1');
  
  if (isLocal) {
    // Use relative URL to leverage Vite proxy (bypasses CORS)
    return '/invocations';
  } else {
    return `${baseUrl}/invocations?qualifier=DEFAULT`;
  }
}

/**
 * Check if we're in local mode
 */
function isLocalMode(): boolean {
  const baseUrl = agentBaseUrl || '';
  return baseUrl.startsWith('http://localhost') || baseUrl.startsWith('http://127.0.0.1');
}

/**
 * Get authentication headers and API key for request
 * - Local mode: Uses API key in request body (no Authorization header)
 * - Production mode: Uses JWT Bearer token in Authorization header
 */
async function getAuthConfig(): Promise<{
  headers: Record<string, string>;
  apiKey?: string;
}> {
  const isLocal = isLocalMode();
  
  if (isLocal) {
    // Local mode - use API key (no JWT needed)
    return {
      headers: {
        'Content-Type': 'application/json',
      },
      apiKey: 'test-api-key-12345', // Matches backend TEST_API_KEY
    };
  } else {
    // Production mode - use JWT Access Token
    const jwtToken = await getAccessToken();
    if (!jwtToken) {
      throw new Error('Not authenticated - no access token available');
    }
    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      },
    };
  }
}

export interface InvokeAgentRequest {
  prompt: string;
  actor_id?: string;
  request_type?: string;
}

export interface InvokeAgentResponse {
  response: string;
}

export interface Tenant {
  tenant_id: string;
  business_name: string;
  business_type: string;
  status: string;
  bucket_name: string;
  kb_id: string;
}

export interface ListTenantsResponse {
  tenants: Tenant[];
  count: number;
}

export interface TabDataResponse {
  [key: string]: any; // Generic tab data (JSON)
}

export const invokeAgent = async (request: InvokeAgentRequest): Promise<InvokeAgentResponse> => {
  try {
    const url = getAgentEndpoint();
    const authConfig = await getAuthConfig();
    
    // Prepare request body
    const requestBody: any = {
      prompt: request.prompt,
      actor_id: request.actor_id,
      request_type: request.request_type || 'chat'
    };
    
    // Add API key for local mode
    if (authConfig.apiKey) {
      requestBody.api_key = authConfig.apiKey;
    }

    // Prepare headers
    const headers: Record<string, string> = { ...authConfig.headers };
    
    // Add AWS-specific headers only in production mode
    if (!isLocalMode()) {
      headers['X-Amzn-Bedrock-AgentCore-Runtime-Session-Id'] = `session-${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
      headers['X-Amzn-Trace-Id'] = `trace-${Date.now()}`;
    }

    console.log('Invoking agent:', { url, isLocal: isLocalMode() });
    console.log('Request payload:', requestBody);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });
    
    console.log('AgentCore response status:', response.status);
    console.log('AgentCore response headers:', Object.fromEntries(response.headers.entries()));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('AgentCore error response:', errorText);
      throw new Error(`AgentCore invocation failed: ${response.status} ${response.statusText} - ${errorText}`);
    }

    // Handle streaming SSE response from AgentCore
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('Response body is not readable');
    }

    const decoder = new TextDecoder();
    let agentResponse = '';
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events (lines ending with \n)
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6); // Remove 'data: ' prefix
            try {
              // Try to parse as JSON (AgentCore sends quoted strings)
              const parsed = JSON.parse(data);
              agentResponse += parsed;
            } catch {
              // If not JSON, use as-is
              agentResponse += data;
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    console.log('Final agent response:', agentResponse);

    return {
      response: agentResponse
    };

  } catch (error: any) {
    console.error('AgentCore invocation error:', error);
    throw new Error(`Failed to invoke agent: ${error.message}`);
  }
};

// NEW: Fetch all tenants for admin dashboard
export const fetchAllTenants = async (userEmail?: string): Promise<ListTenantsResponse> => {
  try {
    const url = getAgentEndpoint();
    const authConfig = await getAuthConfig();
    
    // Validate JWT token only in production mode
    if (!isLocalMode()) {
      try {
        const jwtToken = await getAccessToken();
        if (jwtToken) {
          const parts = jwtToken.split('.');
          if (parts.length >= 2) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const exp = payload.exp;
            const now = Math.floor(Date.now() / 1000);
            if (exp && exp < now) {
              console.error('❌ JWT token is expired. Please sign in again.');
              throw new Error('Token expired - please sign in again');
            }
            console.log('🔍 JWT email in request:', payload.email || payload['cognito:username'] || payload.sub);
            console.log('🔍 JWT token_use:', payload.token_use);
            console.log('🔍 JWT client_id:', payload.client_id);
            console.log('🔍 JWT expires in:', exp ? `${exp - now} seconds` : 'unknown');
          }
        }
      } catch (e) {
        console.warn('Could not decode JWT for logging:', e);
      }
    }

    // Prepare request body
    const requestBody: any = {
      request_type: 'list_tenants'
    };
    
    // Always pass user email so backend can identify the user
    // Cognito Access Tokens don't contain the email claim
    if (userEmail) {
      requestBody.authenticated_email = userEmail;
    }
    
    // Add API key for local mode
    if (authConfig.apiKey) {
      requestBody.api_key = authConfig.apiKey;
    }

    // Prepare headers
    const headers: Record<string, string> = { ...authConfig.headers };
    
    // Add AWS-specific headers only in production mode
    if (!isLocalMode()) {
      headers['X-Amzn-Bedrock-AgentCore-Runtime-Session-Id'] = `admin-${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
    }

    console.log('🔍 Fetching all tenants for admin...');

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No error details');
      console.error('❌ AgentCore response error:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: errorText
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized (401) - Token may be expired or invalid. Please sign out and sign in again.');
      }
      throw new Error(`Failed to fetch tenants: ${response.status} - ${errorText}`);
    }

    // Read streaming response
    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body not readable');

    const decoder = new TextDecoder();
    let jsonData = '';
    let lastValidJson = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6).trim();
          if (data) {
            // Try to parse each data chunk to validate it's complete JSON
            try {
              JSON.parse(data); // Validate it's valid JSON
              lastValidJson = data; // Store the last valid JSON chunk
              jsonData = data; // Use the last complete JSON object
            } catch (e) {
              // If it's not complete JSON yet, accumulate it
              jsonData += data;
            }
          }
        }
      }
    }

    // Use the last valid JSON if we have it, otherwise try to parse accumulated data
    const jsonToParse = lastValidJson || jsonData;
    
    if (!jsonToParse) {
      console.warn('No JSON data found in response');
      return { tenants: [], count: 0 };
    }

    // Parse JSON response (JSON-RPC 2.0 format)
    // Handle potential double-encoding (response might be a JSON string)
    let parsed: any;
    try {
      parsed = JSON.parse(jsonToParse);
      
      // If the parsed result is still a string, parse it again (double-encoded JSON)
      if (typeof parsed === 'string') {
        console.log('Response was double-encoded, parsing again...');
        parsed = JSON.parse(parsed);
      }
    } catch (e) {
      console.error('Failed to parse JSON response:', e);
      console.error('JSON data:', jsonToParse);
      return { tenants: [], count: 0 };
    }
    
    console.log('Fetched tenants response:', parsed);
    console.log('Parsed type:', typeof parsed);
    console.log('Parsed keys:', parsed && typeof parsed === 'object' ? Object.keys(parsed) : 'not an object');
    
    // Handle JSON-RPC 2.0 response format
    // Response can be either:
    // 1. Direct: { tenants: [...], count: 2 }
    // 2. JSON-RPC: { jsonrpc: "2.0", result: { tenants: [...], count: 2 }, id: "null" }
    let tenantsData: any = null;
    
    if (parsed && typeof parsed === 'object') {
      // Check if it's JSON-RPC format
      if ('result' in parsed && parsed.result) {
        // JSON-RPC 2.0 format - extract result
        tenantsData = parsed.result;
        console.log('Extracted result from JSON-RPC response:', tenantsData);
      } else if ('tenants' in parsed) {
        // Direct format - use as is
        tenantsData = parsed;
        console.log('Using direct response format');
      } else {
        console.warn('Unexpected response format:', parsed);
        tenantsData = parsed; // Fallback
      }
    } else {
      console.error('Parsed response is not an object:', typeof parsed, parsed);
      return { tenants: [], count: 0 };
    }
    
    console.log('TenantsData:', tenantsData);
    console.log('TenantsData type:', typeof tenantsData);
    console.log('TenantsData keys:', tenantsData ? Object.keys(tenantsData) : 'null');
    console.log('Tenants count:', tenantsData?.count);
    console.log('Tenants array:', tenantsData?.tenants);

    // Validate response structure
    if (!tenantsData) {
      console.warn('Invalid tenants response: tenantsData is null/undefined', tenantsData);
      return { tenants: [], count: 0 };
    }
    
    if (typeof tenantsData !== 'object') {
      console.warn('Invalid tenants response: not an object', typeof tenantsData, tenantsData);
      return { tenants: [], count: 0 };
    }
    
    // Check if tenants exists and is an array
    const tenants = tenantsData.tenants;
    const count = tenantsData.count;
    
    console.log('Extracted tenants:', tenants);
    console.log('Extracted count:', count);
    
    if (!tenants || !Array.isArray(tenants)) {
      console.warn('Invalid tenants response structure:', tenantsData);
      console.warn('Expected tenants array, got:', typeof tenants, tenants);
      console.warn('Full tenantsData keys:', Object.keys(tenantsData));
      return { tenants: [], count: 0 };
    }

    // Return with explicit structure
    return {
      tenants: tenants,
      count: count || tenants.length
    };

  } catch (error: any) {
    console.error('Fetch tenants error:', error);
    throw new Error(`Failed to fetch tenants: ${error.message}`);
  }
};

// NEW: Fetch tab data for specific tenant
export const fetchTabData = async (
  tenantId: string,
  tabType: 'tab_pulse' | 'tab_journey' | 'tab_actions',
  businessType: string = 'restaurant'
): Promise<TabDataResponse> => {
  try {
    const url = getAgentEndpoint();
    const authConfig = await getAuthConfig();
    
    // Prepare request body
    const requestBody: any = {
      actor_id: tenantId,
      request_type: tabType,
      business_type: businessType
    };
    
    // Add API key for local mode
    if (authConfig.apiKey) {
      requestBody.api_key = authConfig.apiKey;
    }

    // Prepare headers
    const headers: Record<string, string> = { ...authConfig.headers };
    
    // Add AWS-specific headers only in production mode
    if (!isLocalMode()) {
      headers['X-Amzn-Bedrock-AgentCore-Runtime-Session-Id'] = `tab-${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
    }

    console.log(`Fetching ${tabType} for tenant: ${tenantId}`);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${tabType}: ${response.status}`);
    }

    // Read streaming response
    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body not readable');

    const decoder = new TextDecoder();
    let jsonData = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          jsonData += data;
        }
      }
    }

    // Parse JSON response
    const parsed = JSON.parse(jsonData);
    console.log(`${tabType} data for ${tenantId}:`, parsed);

    return parsed;

  } catch (error: any) {
    console.error(`Fetch ${tabType} error:`, error);
    throw new Error(`Failed to fetch ${tabType}: ${error.message}`);
  }
};

// NEW: Onboard tenant (Admin Onboarding)
export const onboardTenant = async (
  tenantId: string,
  businessId: string,
  placeId: string,
  customerEmail: string
): Promise<{success: boolean, message: string, tenant_id?: string}> => {
  try {
    const url = getAgentEndpoint();
    const authConfig = await getAuthConfig();
    
    // Validate JWT token only in production mode
    if (!isLocalMode()) {
      try {
        const jwtToken = await getAccessToken();
        if (jwtToken) {
          const parts = jwtToken.split('.');
          if (parts.length >= 2) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
            const exp = payload.exp;
            const now = Math.floor(Date.now() / 1000);
            if (exp && exp < now) {
              console.error('❌ JWT token is expired. Please sign in again.');
              throw new Error('Token expired - please sign in again');
            }
            
            const tokenClientId = payload.client_id; // Access Token has client_id claim
            const expectedClientId = import.meta.env.VITE_USER_POOL_CLIENT_ID;
            
            console.log('🔍 JWT token_use:', payload.token_use, '(should be "access")');
            console.log('🔍 JWT client_id claim:', tokenClientId || '❌ NOT PRESENT');
            console.log('🔍 Expected client ID from env:', expectedClientId);
            console.log('🔍 Client ID match:', tokenClientId === expectedClientId ? '✅ MATCH' : '❌ MISMATCH');
            console.log('🔍 JWT expires in:', exp ? `${exp - now} seconds` : 'unknown');
            
            // Critical check: AgentCore expects client_id claim in Access Token
            if (!payload.client_id) {
              console.error('❌ CRITICAL: Access Token missing "client_id" claim!');
              console.error('❌ This will cause "Claim client_id value mismatch" error');
            } else if (payload.client_id !== expectedClientId) {
              console.error('❌ CRITICAL: Token "client_id" does not match expected client ID!');
              console.error('❌ Token client_id:', payload.client_id);
              console.error('❌ Expected client ID:', expectedClientId);
            }
          }
        }
      } catch (e) {
        console.warn('Could not decode JWT for logging:', e);
      }
    }

    // Prepare request body
    const requestBody: any = {
      request_type: 'onboard_tenant',
      tenant_id: tenantId,
      business_id: businessId,
      place_id: placeId,
      customer_email: customerEmail
    };
    
    // Add API key for local mode
    if (authConfig.apiKey) {
      requestBody.api_key = authConfig.apiKey;
    }

    // Prepare headers
    const headers: Record<string, string> = { ...authConfig.headers };
    
    // Add AWS-specific headers only in production mode
    if (!isLocalMode()) {
      headers['X-Amzn-Bedrock-AgentCore-Runtime-Session-Id'] = `onboard-${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
    }

    console.log(`🚀 Onboarding tenant: ${tenantId}_${businessId} for customer: ${customerEmail} with place_id: ${placeId}`);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No error details');
      console.error('❌ AgentCore response error:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: errorText
      });
      
      if (response.status === 401) {
        throw new Error('Unauthorized (401) - Token may be expired or invalid. Please sign out and sign in again.');
      }
      throw new Error(`Failed to onboard tenant: ${response.status} - ${errorText}`);
    }

    // Read streaming response
    const reader = response.body?.getReader();
    if (!reader) throw new Error('Response body not readable');

    const decoder = new TextDecoder();
    let jsonData = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          jsonData += data;
        }
      }
    }

    // Parse JSON response
    let parsed;
    try {
      parsed = JSON.parse(jsonData);
    } catch (parseError) {
      console.error('Failed to parse onboard response:', jsonData);
      throw new Error(`Invalid response format: ${jsonData.substring(0, 200)}`);
    }
    
    console.log('Onboard response:', parsed);

    // Check for error in response
    if (parsed.error) {
      return {
        success: false,
        message: parsed.error || parsed.message || 'Unknown error occurred',
        tenant_id: parsed.tenant_id
      };
    }

    return {
      success: parsed.success !== false, // Default to true if not explicitly false
      message: parsed.message || (parsed.success ? 'Tenant onboarded successfully' : 'Unknown error occurred'),
      tenant_id: parsed.tenant_id
    };

  } catch (error: any) {
    console.error('Onboard tenant error:', error);
    console.error('Error details:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    return {
      success: false,
      message: error.message || 'Failed to onboard tenant. Please check the browser console for details.'
    };
  }
};